import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderApi, propertyApi, managementCompanyApi } from '@/services/api';
import BackofficeLayout from "@/components/layouts/BackofficeLayout";
import { DocumentDetail } from "@/components/orders/DocumentDetail";
import { DocumentHeader } from "@/components/orders/DocumentHeader";
import { AiDocumentAnalysis } from "@/components/orders/AiDocumentAnalysis";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const OrderRequestedDocument = () => {
  const { orderId, documentId } = useParams();
  const navigate = useNavigate();

  const { data: orderData, isLoading, error, refetch } = useQuery({
    queryKey: ['backoffice-order-document', orderId, documentId],
    queryFn: async () => {
      if (!orderId) throw new Error('Order ID is required');
      const order = await orderApi.getBackofficeOrder(Number(orderId));
      
      if (!order.requested_documents) {
        throw new Error('No documents found for this order');
      }
      
      // Find the requested document by its ID
      const document = order.requested_documents.find(doc => doc.id === Number(documentId));
      if (!document) {
        throw new Error('Document not found');
      }

      console.log('Retrieved document from API:', document);
      console.log('Document updated_at value:', document.updated_at);

      // Make sure sections and fields have section_id information
      if (document.sections && document.fields) {
        document.sections.forEach(section => {
          if (section.fields) {
            section.fields.forEach(field => {
              // Ensure section_id is set on each field
              field.section_id = section.section_id;
            });
          }
        });
      }

      let propertyAddress = 'No property address found';
      if (order.property_id) {
        try {
          const propertyData = await propertyApi.getById(order.property_id);
          propertyAddress = `${propertyData.address}${propertyData.suite_unit ? ` ${propertyData.suite_unit}` : ''}, ${propertyData.city}, ${propertyData.state} ${propertyData.zip_code}`;
        } catch (error) {
          console.error('Error fetching property:', error);
        }
      }

      // The management company's AI settings decide whether the analysis
      // panel shows on this page.
      let managementCompanyAiSettings = null;
      let managementCompanyId = null;
      if (order.association?.management_company_id) {
        managementCompanyId = order.association.management_company_id;
        try {
          const mc = await managementCompanyApi.getById(managementCompanyId);
          managementCompanyAiSettings = mc.ai_settings;
        } catch (e) {
          console.error('Error fetching management company:', e);
        }
      }

      return {
        document,
        orderId: order.id,
        orderDate: order.created_at.toString(),
        association: order.association,
        propertyAddress,
        ipAddress: order.ip_address,
        managementCompanyId,
        managementCompanyAiSettings,
      };
    }
  });

  if (isLoading) {
    return (
      <BackofficeLayout>
        <div className="flex justify-center items-center h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
        </div>
      </BackofficeLayout>
    );
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="space-y-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <p className="text-gray-500">Error fetching document</p>
        </div>
      </BackofficeLayout>
    );
  }

  if (!orderData) {
    return (
      <BackofficeLayout>
        <div className="space-y-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <p className="text-gray-500">Document not found</p>
        </div>
      </BackofficeLayout>
    );
  }

  console.log('Document value:', orderData.document);
  console.log('Order Data:', orderData);
  console.log('Association Name:', orderData.association?.name);

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Order
          </Button>
        </div>

        <DocumentHeader 
          document={orderData.document}
          orderId={orderData.orderId}
          orderDate={orderData.orderDate}
          propertyAddress={orderData.propertyAddress}
          ipAddress={orderData.ipAddress}
          associationName={orderData.document.association_id ? `[${orderData.association?.name}]` : undefined}
        />

        {orderData.managementCompanyAiSettings?.order_analysis_enabled && (
          <AiDocumentAnalysis
            orderId={orderData.orderId}
            orderDocumentId={orderData.document.id}
            associationId={orderData.association?.id}
            managementCompanyId={orderData.managementCompanyId}
          />
        )}

        <DocumentDetail 
          document={orderData.document} 
          associationName={orderData.document.association_id ? `[${orderData.association?.name}]` : undefined} 
          ipAddress={orderData.ipAddress}
        />
      </div>
    </BackofficeLayout>
  );
};

export default OrderRequestedDocument;
